import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Grid, Card, CardMedia, CardContent, Typography, Container, Modal, Box, IconButton, Button, Pagination } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  outline: 'none',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  maxHeight: '90vh',
  overflowY: 'auto',
};

const imageStyle = {
  maxWidth: '80vw',
  maxHeight: '60vh',
  display: 'block',
};

function RaceImagesPage({ addToCart, cart }) {
  const [images, setImages] = useState([]);
  const [message, setMessage] = useState('');
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const { raceId } = useParams();
  const [raceName, setRaceName] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [userRole, setUserRole] = useState(null);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  const handleOpenModal = (index) => setSelectedImageIndex(index);
  const handleCloseModal = () => {
    setSelectedImageIndex(null);
    setFeedbackMessage('');
  };

  const handlePrevImage = () => {
    setSelectedImageIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : images.length - 1));
    setFeedbackMessage('');
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prevIndex) => (prevIndex < images.length - 1 ? prevIndex + 1 : 0));
    setFeedbackMessage('');
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleAddToCart = (image, raceName) => {
    addToCart(image, raceName);
    setFeedbackMessage('Added to cart!');
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userStatusResponse = await axios.get('/api/users/status');
        if (userStatusResponse.data.loggedIn) {
          setUserRole(userStatusResponse.data.user.role);
        }

        const raceResponse = await axios.get(`/api/races/${raceId}`);
        setRaceName(raceResponse.data.name);

        const imagesResponse = await axios.get(`/api/photos?race_id=${raceId}&page=${page}&limit=20`);
        setImages(imagesResponse.data.photos);
        setTotalPages(Math.ceil(imagesResponse.data.total / 20));
      } catch (error) {
        setMessage('Error fetching data: ' + (error.response?.data || error.message));
      }
    };
    fetchData();
  }, [raceId, page]);

  const selectedImage = selectedImageIndex !== null ? images[selectedImageIndex] : null;

  const isImageInCart = (imageId) => {
    return cart.some((item) => item.id === imageId);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Images for {raceName}
      </Typography>
      {message && <Typography color="error">{message}</Typography>}
      {images.length > 0 ? (
        <>
          <Grid container spacing={4}>
            {images.map((image, index) => (
              <Grid item key={image.id} xs={12} sm={6} md={4} lg={3}>
                <Card>
                  <CardMedia
                    component="img"
                    height="200"
                    image={`/api/photos/thumb/${encodeURIComponent(image.thumbnail_filename)}`}
                    alt={image.original_name}
                    onClick={() => handleOpenModal(index)}
                    sx={{ cursor: 'pointer' }}
                  />
                  <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {image.original_name}
                    </Typography>
                    <IconButton onClick={() => handleAddToCart(image, raceName)} disabled={isImageInCart(image.id)}>
                      <ShoppingCartIcon />
                    </IconButton>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Pagination count={totalPages} page={page} onChange={handlePageChange} />
          </Box>
        </>
      ) : (
        <Typography>No images found for this race.</Typography>
      )}
      <Modal
        open={!!selectedImage}
        onClose={handleCloseModal}
        aria-labelledby="image-modal-title"
        aria-describedby="image-modal-description"
      >
        <Box sx={modalStyle}>
          <IconButton onClick={handlePrevImage} sx={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}><ArrowBackIosNewIcon /></IconButton>
          {selectedImage && (
            <>
              <img
                src={`/api/photos/${selectedImage.guid}`}
                alt={selectedImage.original_name}
                style={imageStyle}
              />
              <Button variant="contained" color="primary" onClick={() => handleAddToCart(selectedImage, raceName)} sx={{ mt: 2 }} disabled={isImageInCart(selectedImage.id)}>
                {isImageInCart(selectedImage.id) ? 'Already in Cart' : 'Add to Cart ($4.99)'}
              </Button>
              {feedbackMessage && <Typography color="primary" sx={{ mt: 1 }}>{feedbackMessage}</Typography>}
              {userRole === 'admin' && (
                <Box sx={{ width: '100%', mt: 2 }}>
                  <Grid container spacing={1}>
                    {Object.entries(selectedImage).map(([key, value]) => (
                      <Grid item xs={12} sm={6} key={key}>
                        <Typography variant="body2"><strong>{key}:</strong> {String(value)}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </>
          )}
          <IconButton onClick={handleNextImage} sx={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}><ArrowForwardIosIcon /></IconButton>
          <IconButton onClick={handleCloseModal} sx={{ position: 'absolute', top: 10, right: 10, color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}><CloseIcon /></IconButton>
        </Box>
      </Modal>
    </Container>
  );
}

export default RaceImagesPage;